/**
 * Tracks: where the boat has actually been.
 *
 * The mirror image of a route. A route is a plan, edited by hand, checked
 * against the chart, and shared; a track is a record, appended to by the
 * receiver and never edited. They are kept apart deliberately — a track that
 * could be edited would stop being evidence of anything, and the one use of a
 * track that really matters is retracing your own way out of somewhere in poor
 * visibility, which is worth nothing if the line has been tidied up.
 *
 * Two rules the rest of the app depends on:
 *
 * - **A track knows whether it is real.** A track recorded while the debug
 *   simulation is on carries `simulated: true` for good, and every surface that
 *   shows it says so. The flag is set the moment one simulated fix lands and is
 *   never cleared, so a track that is part real and part invented is marked as
 *   invented — the conservative direction, and the same rule the simulated fix
 *   itself follows.
 * - **Points are thinned on the way in, not on the way out.** A one-second fix
 *   rate over a day is 86 400 points, most of them noise about a boat sitting
 *   at a wharf. `shouldRecord` is what keeps a track small enough to store and
 *   draw; it drops points that say nothing, never points that say something.
 *
 * All distances metres, speeds metres per second, times epoch milliseconds.
 */

import {
  type LatLon,
  boundsOf,
  greatCircleDistance,
  isValidLatLon,
} from './geo';

export interface TrackPoint extends LatLon {
  /** Epoch milliseconds. */
  t: number;
  /** Speed over ground when recorded, or null where the receiver did not say. */
  speedMs?: number | null;
  /** Course over ground when recorded, degrees true, or null. */
  courseDeg?: number | null;
}

export interface Track {
  id: string;
  name: string;
  points: TrackPoint[];
  /** Epoch milliseconds the recording started. */
  startedAt: number;
  /** Epoch milliseconds of the last change — the sort key for the listing. */
  updatedAt: number;
  /**
   * Recorded, wholly or partly, from a simulated position.
   *
   * Never cleared once set. A track that is part invented is marked invented.
   */
  simulated: boolean;
  notes?: string;
}

/**
 * How far the boat must move before another point is kept, metres.
 *
 * Fifteen metres is a couple of boat lengths and comfortably outside consumer
 * GPS noise at anchor, which is what would otherwise fill a track with a
 * scribble.
 */
export const TRACK_MIN_MOVE_M = 15;

/**
 * Shortest gap between kept points, milliseconds.
 *
 * A rate limit, applied before the distance test: at twenty knots the boat
 * covers fifteen metres every second and a half, and a track of the whole
 * Eastern Shore at that resolution is neither storable nor drawable.
 */
export const TRACK_MIN_INTERVAL_MS = 5_000;

/**
 * Longest gap before a point is kept regardless, milliseconds.
 *
 * A stationary boat still gets a point a minute. Without it a five-hour stop
 * shows as a single point and the track's own timestamps say the boat teleported
 * — the elapsed time of a passage would be right and every other reading from it
 * wrong.
 */
export const TRACK_IDLE_INTERVAL_MS = 60_000;

/**
 * Most points a single track holds.
 *
 * At the thinning above this is roughly a week under way. It exists so a
 * forgotten recording cannot grow until IndexedDB refuses the write — losing the
 * newest point quietly is bad, losing the whole track is worse.
 */
export const TRACK_MAX_POINTS = 20_000;

export interface TrackRecordOptions {
  minMoveM: number;
  minIntervalMs: number;
  idleIntervalMs: number;
  maxPoints: number;
}

export const DEFAULT_TRACK_OPTIONS: TrackRecordOptions = {
  minMoveM: TRACK_MIN_MOVE_M,
  minIntervalMs: TRACK_MIN_INTERVAL_MS,
  idleIntervalMs: TRACK_IDLE_INTERVAL_MS,
  maxPoints: TRACK_MAX_POINTS,
};

export function createTrack(id: string, name = 'New track', now = Date.now()): Track {
  return { id, name, points: [], startedAt: now, updatedAt: now, simulated: false };
}

/**
 * Whether a new point is worth keeping.
 *
 * The rate limit comes first and the idle rule last, which is the order that
 * makes both of them mean what they say: nothing is kept inside the minimum
 * interval, and after the idle interval something is kept whether or not the
 * boat has moved.
 */
export function shouldRecord(
  last: TrackPoint | undefined,
  next: TrackPoint,
  options: TrackRecordOptions = DEFAULT_TRACK_OPTIONS,
): boolean {
  if (!last) return true;

  const elapsedMs = next.t - last.t;
  // Out-of-order fixes happen when a receiver replays a buffered position.
  // Keeping them would draw the track backwards, so they are dropped.
  if (elapsedMs < 0) return false;
  if (elapsedMs < options.minIntervalMs) return false;
  if (greatCircleDistance(last, next) >= options.minMoveM) return true;
  return elapsedMs >= options.idleIntervalMs;
}

/**
 * Append a point if it is worth keeping.
 *
 * Returns the *same object* when nothing was recorded. React state is updated
 * from this on every fix, and returning a fresh object each time would re-render
 * the app — and redraw the chart — once a second for no reason.
 */
export function appendPoint(
  track: Track,
  point: TrackPoint,
  options: TrackRecordOptions = DEFAULT_TRACK_OPTIONS,
): Track {
  if (!isValidLatLon(point) || !Number.isFinite(point.t)) return track;
  if (track.points.length >= options.maxPoints) return track;
  if (!shouldRecord(track.points[track.points.length - 1], point, options)) return track;

  return { ...track, points: [...track.points, point], updatedAt: point.t };
}

/** Mark a track as containing invented positions. One way only. */
export function markSimulated(track: Track): Track {
  return track.simulated ? track : { ...track, simulated: true };
}

/** Distance run along the track, metres. */
export function trackDistanceM(track: Pick<Track, 'points'>): number {
  let total = 0;
  for (let i = 1; i < track.points.length; i++) {
    total += greatCircleDistance(track.points[i - 1], track.points[i]);
  }
  return total;
}

export interface TrackStats {
  points: number;
  /** Distance run along the track, metres. */
  distanceM: number;
  /** First point to last point, seconds. */
  durationS: number;
  /**
   * Distance over elapsed time, metres per second — null for a track with no
   * elapsed time.
   *
   * Speed made good over the whole recording, including every stop. It is not
   * the speed the boat was doing, and a panel showing it has to say which it is.
   */
  averageSpeedMs: number | null;
  /**
   * Fastest speed the receiver reported, or null if it never reported one.
   *
   * From the fixes rather than from the geometry: distance between two thinned
   * points divided by their interval understates a turn and overstates nothing,
   * and a receiver's own figure is better than either.
   */
  maxSpeedMs: number | null;
}

export function trackStats(track: Pick<Track, 'points'>): TrackStats {
  const { points } = track;
  const distanceM = trackDistanceM(track);
  const durationS = points.length >= 2
    ? (points[points.length - 1].t - points[0].t) / 1000
    : 0;

  let maxSpeedMs: number | null = null;
  for (const point of points) {
    if (typeof point.speedMs === 'number' && Number.isFinite(point.speedMs)) {
      maxSpeedMs = maxSpeedMs === null ? point.speedMs : Math.max(maxSpeedMs, point.speedMs);
    }
  }

  return {
    points: points.length,
    distanceM,
    durationS,
    averageSpeedMs: durationS > 0 ? distanceM / durationS : null,
    maxSpeedMs,
  };
}

/** Bounding box of the track, or null when it has no points. */
export function trackBounds(
  track: Pick<Track, 'points'>,
): { south: number; west: number; north: number; east: number } | null {
  return boundsOf(track.points);
}

/** Reject tracks that cannot be stored. */
export function validateTrack(track: Track): string[] {
  const problems: string[] = [];
  if (!track.name.trim()) problems.push('Track name is required');
  if (!Number.isFinite(track.startedAt)) problems.push('Track start time is invalid');
  // Every point, not the first bad one: a track is stored whole or not at all,
  // and half a track drawn on a chart is a line the boat never took.
  track.points.forEach((point, i) => {
    if (!isValidLatLon(point) || !Number.isFinite(point.t)) {
      problems.push(`Track point ${i + 1} is invalid`);
    }
  });
  return problems;
}

const xml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * The track as GPX 1.1.
 *
 * The one format every chartplotter, OpenCPN and phone app will read, and the
 * reason a track saved here is not trapped here. Positions go out in WGS-84
 * decimal degrees and times in UTC, which is what the format specifies — rule 1
 * again: no display unit ever leaves the app in stored data.
 *
 * A simulated track says so in its description **and** in its name. The
 * description alone would be dropped by half the software that reads this.
 */
export function trackToGpx(track: Track, creator = 'Out and a Boat'): string {
  const name = track.simulated ? `${track.name} (SIMULATED)` : track.name;
  const description = track.simulated
    ? 'SIMULATED — recorded from a made-up position, not from a GPS. Not a record of where any boat has been.'
    : 'Recorded track.';

  const points = track.points.map((point) => [
    `      <trkpt lat="${point.lat.toFixed(7)}" lon="${point.lon.toFixed(7)}">`,
    `        <time>${new Date(point.t).toISOString()}</time>`,
    '      </trkpt>',
  ].join('\n')).join('\n');

  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<gpx version="1.1" creator="${xml(creator)}"`,
    '     xmlns="http://www.topografix.com/GPX/1/1"',
    '     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"',
    '     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">',
    '  <metadata>',
    `    <name>${xml(name)}</name>`,
    `    <time>${new Date(track.startedAt).toISOString()}</time>`,
    '  </metadata>',
    '  <trk>',
    `    <name>${xml(name)}</name>`,
    `    <desc>${xml(description)}</desc>`,
    '    <trkseg>',
    // An empty segment rather than a blank line: a track with no points is a
    // valid, if useless, GPX file, and emitting broken XML for it would be worse.
    ...(points ? [points] : []),
    '    </trkseg>',
    '  </trk>',
    '</gpx>',
  ];
  return `${lines.join('\n')}\n`;
}

/** A filename that will not upset any filesystem. */
export function trackFilename(track: Track): string {
  const slug = track.name.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'track';
  return `${track.simulated ? 'simulated-' : ''}${slug}.gpx`;
}
